import { useState } from "react";
import type { AutopilotView } from "@auto-pools/core";
import { api, ApiError } from "../api/client";
import { useAutopilot } from "./autopilotContext";

/**
 * The run was sealed under strategy rules other than the server's current ones. `resumeRun`
 * refuses such a run (AUTOPILOT_NOT_RESUMABLE), so Resume is never offered for it; a new Start is.
 */
export function rulesChangedSinceStart(rules: AutopilotView["rules"]): boolean {
  return rules.runRulesHash !== null && rules.runRulesHash !== rules.hash;
}

/**
 * Shared `POST /autopilot/resume` submit logic (review D2), used by both the drawdown banner and
 * the Autopilot page header.
 *
 * Every *distinct* Resume needs its own idempotency key: reusing one lets the server treat a later,
 * unrelated Resume as a replay of an earlier command and silently no-op it. The key rotates only
 * after a request *succeeds*: `resuming` still clears on failure (in `finally`), so the button is
 * clickable again, but the key itself is kept. That is deliberate, not a missed rotation — nothing
 * was `remember()`-ed server-side for a failed attempt, so reusing the key lets a genuine retry
 * re-attempt the same command cleanly instead of silently replaying a stale success it never saw.
 * Only once a request actually completes successfully does the next click need a fresh key, so an
 * unrelated Resume issued afterward is never mistaken for a replay of the one that just finished.
 *
 * The server's response already carries the fresh view, so it is applied straight into the shared
 * resource via `updateData` instead of triggering a separate `reload()`. That avoids a race where a
 * concurrent 15 s poll, started before the mutation, would land after it and overwrite the just
 * -applied result with a stale one (`useResource`'s mutation generation discards it instead).
 */
export function useAutopilotResume() {
  const autopilot = useAutopilot();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function resume() {
    if (resuming) return;
    setResuming(true);
    setError(null);
    try {
      const updated = await api<AutopilotView>("/autopilot/resume", {
        method: "POST",
        body: { idempotencyKey },
        sensitive: true,
      });
      autopilot.updateData(() => updated);
      setIdempotencyKey(crypto.randomUUID());
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : reason instanceof Error
            ? reason.message
            : "Resume was not accepted",
      );
    } finally {
      setResuming(false);
    }
  }

  return { resume, resuming, error };
}
